'use client'

import { Trans } from '@lingui/react/macro'
import { Box, Paper, Typography, Chip, alpha, Dialog, DialogContent, DialogActions, Button, Divider } from '@mui/material'
import VerifiedIcon from '@mui/icons-material/Verified'
import ContactSupportIcon from '@mui/icons-material/ContactSupport'
import type { EthicsFormSubmission } from '../model/types'
import { getStatusConfig } from '../lib/statusConfig'
import { getFieldDisplayValue } from '../lib/helpers'

type EthicsResponseModalProps = {
  submission: EthicsFormSubmission
  onClose: () => void
}

export function EthicsResponseModal({ submission, onClose }: EthicsResponseModalProps) {
  const config = getStatusConfig(submission.status)
  const descriptionResp = submission.responses.find((r) => r.fieldId === 'description')
  const descriptionValue = descriptionResp ? getFieldDisplayValue(descriptionResp.value) : ''

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(25, 28, 29, 0.4)', backdropFilter: 'blur(8px)' } } }}
    >
      <DialogContent sx={{ px: 4, pt: 4, pb: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', color: 'text.primary', lineHeight: 1.2 }}>
              <Trans>Response to your question</Trans>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              <Trans>Submitted on</Trans> {new Date(submission.submittedAt || submission.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
          <Chip
            label={config.label}
            color={config.muiColor}
            size="small"
            sx={{ fontWeight: 700, fontSize: '0.7rem', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}
          />
        </Box>

        {/* Category + Question */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <ContactSupportIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.1em', color: 'text.secondary' }}>
              {submission.template.title}
            </Typography>
          </Box>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              bgcolor: 'background.default',
              borderLeft: '4px solid',
              borderLeftColor: (theme) => alpha(theme.palette.primary.main, 0.2),
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', lineHeight: 1.6 }}>
              {descriptionValue}
            </Typography>
          </Paper>
        </Box>

        {/* Ethics Response */}
        {submission.ethicsResponse && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <VerifiedIcon sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.1em', color: 'success.main' }}>
                <Trans>Response</Trans>
              </Typography>
            </Box>
            <Paper
              elevation={0}
              sx={{ p: 3, borderRadius: 3, bgcolor: (theme) => alpha(theme.palette.success.main, 0.03), position: 'relative', overflow: 'hidden' }}
            >
              {submission.respondedBy && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    {submission.respondedBy.userInfo.surname} {submission.respondedBy.userInfo.name}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    &bull;
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {submission.respondedAt && new Date(submission.respondedAt).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', lineHeight: 1.4 }}>
                {submission.ethicsResponse}
              </Typography>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 4, py: 2, bgcolor: 'background.default' }}>
        <Button onClick={onClose} sx={{ fontWeight: 700, borderRadius: 2 }}>
          <Trans>Close</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  )
}
